import vehicleTools from "../../../helpers/vehicleTools";

// Espaces insécables (U+00A0/U+202F) insérées par toLocaleString("fr-FR") —
// échappements explicites plutôt que les caractères littéraux, invisibles à
// l'édition et faciles à casser sans s'en rendre compte.
const ESPACES_INSECABLES = /[\u00A0\u202F]/g;

// Montant en euros, format FR (séparateurs de milliers, 2 décimales) —
// partagé par le tableau écran (PaymentTable) et le PDF (PaymentTableDocument)
// pour un arrondi/affichage cohérent des montants du récapitulatif.
// `toLocaleString("fr-FR")` sépare les milliers par une espace insécable
// (U+00A0/U+202F selon l'environnement) : la police utilisée par
// @react-pdf/renderer ne porte pas ce glyphe et l'affiche comme un caractère
// erroné (barre oblique). On la remplace par une espace normale.
export const fmtEuro = (v) =>
  `${Number(v || 0)
    .toLocaleString("fr-FR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    .replace(ESPACES_INSECABLES, " ")} €`;

// Nom du fichier PDF du récapitulatif : référence de la demande + timestamp
// de génération, pour ne pas écraser les versions précédentes envoyées au
// financeur (ex: recap-DEM-2026-042-1783456789012.pdf).
export function buildRecapFileName(reference) {
  return `recap-${reference}-${Date.now()}.pdf`;
}

// LS-682 — libellé de la VR finale du tableau imprimé/transmis, « telle que
// saisie » (règle actée en réunion du 13/08/2026, tous financeurs) : certains
// financeurs montent leur dossier sur le taux affiché, jamais sur sa
// contre-valeur en euros. Pourcentage si l'utilisateur a saisi un pourcentage
// (couple historique vrUnite/vrValeurSaisie, ou taux VR manuel LS-670),
// montant en euros sinon — y compris le 1 € par défaut. L'ordre de priorité
// suit la résolution côté API (ResoudreVR) : couple saisi > taux manuel >
// montant conservé. Fonction pure, hors PaymentTableDocument : react-pdf ne
// se rend pas sous jsdom.
export function libelleVrFinale(p) {
  if (p.vrValeurSaisie != null && p.vrUnite === "POURCENTAGE") {
    return `${fmtPourcent(p.vrValeurSaisie)} %`;
  }
  if (p.vrValeurSaisie != null && p.vrUnite === "EURO") {
    return fmtEuro(p.vrValeurSaisie);
  }
  if (p.tauxVrManuel != null) {
    // Fraction API (0-1) → pourcentage, arrondi à 2 décimales (0.025 → 2,5).
    return `${fmtPourcent(Math.round(p.tauxVrManuel * 10000) / 100)} %`;
  }
  return fmtEuro(p.residualValue ?? parseFloat(p.vr));
}

// Pourcentage au format FR (virgule décimale, sans décimales forcées : 1 → "1",
// 2.5 → "2,5"), même traitement de l'espace insécable que fmtEuro.
const fmtPourcent = (v) =>
  Number(v || 0)
    .toLocaleString("fr-FR")
    .replace(ESPACES_INSECABLES, " ");

// Construit les lignes du récapitulatif (devis × périodes) avec toutes les
// valeurs déjà calculées (prix, TVA, type). Sortie PURE : aucun hook, pour
// pouvoir être rendue par `pdf().toBlob()` hors contexte React.
// `getTVAByCatalog` (hook useTVA) et `constantData` (store) sont injectés par
// l'appelant, qui dispose du contexte React.
export function buildRecapRows({
  finances,
  quotationSources,
  constantData,
  getTVAByCatalog,
}) {
  // Croisement : pour chaque devis de la demande (avec ses périodes), retrouver
  // l'objet devis complet (véhicule, prix…) dans les sources fournies.
  // `quotationSources` peut contenir le même devis dans plusieurs groupes
  // (ex: demande + résultats de recherche) : on indexe par uuid pour éviter
  // les lignes dupliquées dans le récapitulatif (écran et PDF).
  const quotationsByUuid = new Map();
  (quotationSources || []).forEach((group) => {
    (group.quotations || []).forEach((sq) => {
      if (!quotationsByUuid.has(sq.uuid)) {
        quotationsByUuid.set(sq.uuid, sq);
      }
    });
  });

  const qs = (finances?.quotations || [])
    .map((f) => {
      const sq = quotationsByUuid.get(f.quotation);
      return sq ? { ...sq, periods: f.periods } : null;
    })
    .filter(Boolean);

  return qs.map((q) => {
    const { TVA, TVA_INVERT } = getTVAByCatalog(q.vehicle.catalog.uuid);
    const vehiclePrices = vehicleTools.calcVehiclePrices(
      q.vehicle,
      TVA,
      TVA_INVERT,
      true,
    );
    const priceHT = Math.round(vehiclePrices.priceHT * 100) / 100;
    const optHT = vehicleTools.calcOptHT(q, TVA);
    const vehicleTVA = priceHT * (q.vehicle.type.id == 2 ? 0 : TVA);
    const tva =
      Math.round(
        (vehicleTVA + vehicleTools.calcTvaMarOpt(q, TVA, TVA_INVERT)) * 100,
      ) / 100;
    const typeObj = (constantData.VEHICLE_TYPES || []).find(
      (v) => v.id == q.vehicle.type.id,
    );
    const quantity = parseInt(q.quantity) || 1;

    // LS-680 — l'API renvoie des montants de VR déjà totaux pour la ligne de
    // devis (quantity véhicules confondus, cf. LS-652). Les composants d'affichage
    // (PaymentTable, PaymentTableDocument) répètent chaque période sur `quantity`
    // lignes véhicule et ré-additionnent la VR à chaque itération pour reconstituer
    // le total : ils attendent donc une VR PAR VÉHICULE en entrée. On la déduit ici,
    // une seule fois, pour que la division/re-multiplication reste cohérente
    // (division par 1 = no-op pour quantity = 1, non-régression).
    // `vrValeurSaisie` suit la même règle quand la saisie est en euros (montant
    // global de la ligne, affiché tel quel par libelleVrFinale — LS-682) ; une
    // saisie en pourcentage est invariante d'échelle et ne se divise jamais.
    const periods = (q.periods || []).map((p) => ({
      ...p,
      vr: p.vr != null ? Math.round((p.vr / quantity) * 100) / 100 : p.vr,
      residualValue:
        p.residualValue != null
          ? Math.round((p.residualValue / quantity) * 100) / 100
          : p.residualValue,
      vrValeurSaisie:
        p.vrUnite === "EURO" && p.vrValeurSaisie != null
          ? Math.round((p.vrValeurSaisie / quantity) * 100) / 100
          : p.vrValeurSaisie,
    }));

    return {
      reference: q.reference,
      quantity,
      typeId: q.vehicle.type.id,
      typeName: typeObj ? typeObj.name : "",
      priceHT,
      optHT,
      marHT: q.vehicle.inroadCost,
      cgCarb: q.vehicle.greyCardCost,
      bm: q.vehicle.malusBonusEco,
      tva,
      priceTTC: Math.round(q.vehicle.priceNetTTC * 100) / 100,
      investAmtHT: q.vehicle.investAmtHT,
      periods,
    };
  });
}
